import React from "react";
import { useNavigate } from "react-router-dom";
import { useGetProgramsQuery } from "../../../utils/api/endpoints/programsApi";

type Day = { date: Date; label: string };

function startOfWeek(d: Date) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = (day === 0 ? -6 : 1) - day; // Monday as first day
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function addDays(d: Date, n: number) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

const formatYMD = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isUnassigned = (pau: any): boolean => {
  if (pau === null || pau === undefined) return true;
  if (pau === 0 || pau === "0") return true;
  if (pau === "[]") return true;
  try {
    if (Array.isArray(pau)) return pau.length === 0;
    const parsed = JSON.parse(pau);
    return Array.isArray(parsed) ? parsed.length === 0 : true;
  } catch {
    return true;
  }
};

export default function UpcomingProgramsCalendar({
  items: provided
}: {
  items?: Array<{
    id: number;
    title: string;
    expected_start_date: string;
    is_activated?: boolean;
    privateAccessUsers?: any;
    isOverdue?: boolean;
  }>;
} = {}) {
  const navigate = useNavigate();
  const [anchor, setAnchor] = React.useState<Date>(startOfWeek(new Date()));

  const { data } = useGetProgramsQuery({
    activated: ["Active", "Inactive"],
    limit: 200,
    page: 1,
    type: "PTAAS"
  } as any);

  const days: Day[] = React.useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const date = addDays(anchor, i);
      return { date, label: date.getDate().toString() };
    });
  }, [anchor]);

  // Split provided items into overdue and upcoming
  const overdueItems = React.useMemo(() => {
    if (provided && provided.length > 0) {
      return provided.filter(p => p.isOverdue);
    }
    // Fallback: derive from API data
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return (data?.programs || []).filter((p: any) => {
      if (!p.expected_start_date) return false;
      const dateMatch = String(p.expected_start_date).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!dateMatch) return false;
      const [, year, month, day] = dateMatch;
      const dt = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (isNaN(dt.getTime())) return false;
      if (dt >= today) return false; // only past dates
      const pau = p.private_access_users ?? p.privateAccessUsers;
      return isUnassigned(pau);
    });
  }, [provided, data]);

  const eventsByDay = React.useMemo(() => {
    const map: Record<string, any[]> = {};
    days.forEach(d => (map[formatYMD(d.date)] = []));

    const items = provided && provided.length ? provided.filter(p => !p.isOverdue) : (data?.programs || []);
    items.forEach((p: any) => {
      const startStr = p.expected_start_date;
      if (!startStr) return;

      const dateMatch = String(startStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!dateMatch) return;

      const [, year, month, day] = dateMatch;
      const dt = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (isNaN(dt.getTime())) return;

      // Only programs with no researchers assigned
      const pau = p.private_access_users ?? p.privateAccessUsers;
      if (!isUnassigned(pau)) return;

      const key = formatYMD(dt);
      if (map[key]) map[key].push(p);
    });
    return map;
  }, [provided, data, days]);

  const weekTitle = React.useMemo(() => {
    const start = days[0].date;
    const end = days[6].date;
    const fmt = (d: Date) =>
      d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return `${fmt(start)} - ${fmt(end)}`;
  }, [days]);

  return (
    <div className="rounded-2xl bg-white p-4 shadow-lg ring-1 ring-gray-100">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-[#0B1739]">Pending Pentester Assignment</h3>
          {overdueItems.length > 0 && (
            <span className="rounded-full bg-red-900 px-2.5 py-0.5 text-xs font-bold text-white">
              {overdueItems.length} overdue
            </span>
          )}
          <span className="text-sm text-gray-600">{weekTitle}</span>
        </div>
        <div className="flex items-center gap-2">
          <button className="rounded-md border px-3 py-1.5" onClick={() => setAnchor(startOfWeek(new Date()))}>Today</button>
          <button className="rounded-md border px-3 py-1.5" onClick={() => setAnchor(addDays(anchor, -7))}>{"<"}</button>
          <button className="rounded-md border px-3 py-1.5" onClick={() => setAnchor(addDays(anchor, 7))}>{">"}</button>
        </div>
      </div>

      {/* Overdue programs strip */}
      {overdueItems.length > 0 && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3">
          <div className="mb-2 flex items-center gap-2">
            <span className="text-sm font-semibold text-red-800">⚠ Overdue — No Pentester Assigned</span>
            <span className="text-xs text-red-600">These programs have already passed their start date</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {overdueItems.map((p: any) => {
              const startStr = p.expected_start_date;
              const dateMatch = startStr ? String(startStr).match(/^(\d{4})-(\d{2})-(\d{2})/) : null;
              let daysAgo = 0;
              if (dateMatch) {
                const [, year, month, day] = dateMatch;
                const dt = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                daysAgo = Math.floor((today.getTime() - dt.getTime()) / (1000 * 60 * 60 * 24));
              }
              return (
                <button
                  key={p.id ?? p.program_id}
                  className="flex items-center gap-2 rounded-lg border border-red-300 bg-white px-3 py-2 text-left text-sm shadow-sm hover:bg-red-50 transition-colors"
                  onClick={() => navigate(`/dashboard/programs/${p.id ?? p.program_id}`)}
                >
                  <span className="font-medium text-red-900">{p.title ?? p.program_title}</span>
                  <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-xs text-red-700">
                    {daysAgo === 0 ? "due today" : `${daysAgo}d ago`}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-7 gap-4">
        {days.map(d => {
          const key = formatYMD(d.date);
          const items = eventsByDay[key] || [];
          return (
            <div key={key} className="min-h-[220px]">
              <div className="mb-2 text-center text-sm font-medium text-gray-700">
                {d.date.toLocaleDateString("en-US", { weekday: "short" })}
              </div>
              <div className="mb-2 text-center text-2xl font-semibold text-[#0B1739]">{d.label}</div>
              <div className="h-[180px] overflow-y-auto rounded-lg border border-gray-100 p-2">
                {items.length === 0 ? (
                  <div className="px-2 py-4 text-center text-xs text-gray-400">No events</div>
                ) : (
                  items.map(p => {
                    const startStr = p.expected_start_date as string;
                    const dateMatch = String(startStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
                    if (!dateMatch) return null;

                    const [, year, month, day] = dateMatch;
                    const dt = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const diffDays = Math.floor((dt.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

                    let bg = "#E5E7EB";
                    let fg = "#111827";
                    if (diffDays === 2) { bg = "#DBEAFE"; fg = "#1D4ED8"; }
                    else if (diffDays === 1) { bg = "#BBF7D0"; fg = "#15803D"; }
                    else if (diffDays === 0) { bg = "#FECACA"; fg = "#B91C1C"; }

                    return (
                      <button
                        key={p.id}
                        className="mb-2 w-full rounded-lg px-3 py-2 text-left text-sm"
                        style={{ backgroundColor: bg, color: fg }}
                        onClick={() => navigate(`/dashboard/programs/${p.id}`)}
                      >
                        <span className="mr-2 inline-flex h-4 w-4 align-middle" aria-hidden>
                          <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2c-.6 0-1 .4-1 1v1.1C7.6 4.6 5 7.5 5 11v1l-1.6 3.2c-.2.4.1.8.5.8H9v3c0 .6.4 1 1 1h4c.6 0 1-.4 1-1v-3h5.1c.4 0 .7-.4.5-.8L19 12v-1c0-3.5-2.6-6.4-6-6.9V3c0-.6-.4-1-1-1z" />
                          </svg>
                        </span>
                        <span className="align-middle">{p.title}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 border-t border-gray-200 pt-4 flex items-center justify-center gap-16 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: "#DBEAFE" }} />
          <span>Program Assign due in 2 days</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: "#BBF7D0" }} />
          <span>Program Assign due in 1 day</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: "#FECACA" }} />
          <span>Program Assign due today</span>
        </div>
      </div>
    </div>
  );
}
